// BLOCKED-PRESENTATION (2026-07-18): the ONE brain of the blocking model.
// Everything that DECIDES about a blocked main msg or sub row lives here —
// the status enum, the display phrasing, the time-side reading, and the
// "edit password overrides the time window" pin rule. Consumers present:
//   LockedScreen  (main msg)  — full-page locked document, session unlock
//   BlockedPreview (sub rows) — preview leaf, in-place get_locked unlock
//   row bridge    (display.access.blocked passthrough for variant awareness)
//
// editLocked is deliberately NOT a blocked state: it never hides content,
// only edit/move capability (that is permissions/possession territory).

import type { MsgAccessGrant } from '@/msgdeskRuntime/state'

export type BlockedState = 'none' | 'showpinned' | 'time-early' | 'time-elapsed'

export interface BlockedPresentation {
  blocked: BlockedState
  /** Ready display sentence ('' when none). */
  text: string
  /** Which pin opens it: time locks accept the EDIT password only. */
  pinKind: 'show-or-edit' | 'edit-only' | null
}

const TEXTS: Record<Exclude<BlockedState, 'none'>, string> = {
  'showpinned': 'This message is protected.',
  'time-early': 'This message is not open yet.',
  'time-elapsed': 'This message is no longer available.',
}

export function blockedPresentation(input: {
  contentLocked: boolean
  timeLocked: boolean
  timeSide?: 'early' | 'elapsed'
}): BlockedPresentation {
  if (!input.contentLocked) return { blocked: 'none', text: '', pinKind: null }

  const blocked: BlockedState = input.timeLocked
    ? (input.timeSide === 'early' ? 'time-early' : 'time-elapsed')
    : 'showpinned'

  return {
    blocked,
    text: TEXTS[blocked],
    pinKind: input.timeLocked ? 'edit-only' : 'show-or-edit',
  }
}

/** Main-msg convenience: derive from the semantic grant alone. */
export function blockedPresentationFromGrant(grant: MsgAccessGrant): BlockedPresentation {
  return blockedPresentation({
    contentLocked: !grant.capabilities.can_view_content,
    timeLocked: grant.reason === 'time',
    timeSide: grant.time_side,
  })
}
